import { Injectable } from '@angular/core';

import { DOC_TYPES, FoundDocument, PublishedPack, TYPE_ORDER } from '~/models/document-finder.models';
import { DocumentFileService } from '~/services/document-file.service';
import { DocumentFinderApiService } from '~/services/document-finder-api.service';
import { DocumentFinderService } from '~/services/document-finder.service';
import { downloadBlob } from '~/utils/download';
import { ZipEntry, createZip, safeFileName } from '~/utils/zip';

export interface PackZipResult {
    fileName: string;
    /** Number of document files written to the archive */
    added: number;
    /** Documents whose content could not be fetched (listed in the archive's README) */
    failed: FoundDocument[];
}

/**
 * Publishes the confirmed pack and downloads it as a ZIP:
 *
 *   <PK-id> <pack name>.zip
 *   ├─ README.txt                 pack details, note, and any files that failed to download
 *   ├─ manifest.csv               one row per document (same as "Export CSV")
 *   ├─ 01 Process flow/As-Is (ECC)/…
 *   ├─ 02 Business Process Design/To-Be (S4HANA)/…
 *   └─ …
 */
@Injectable({ providedIn: 'root' })
export class PackExportService {
    public packName: string = '';
    public packNote: string = '';
    /** True while files are being fetched and zipped */
    public busy: boolean = false;

    constructor(private df: DocumentFinderService, private files: DocumentFileService, private api: DocumentFinderApiService) {}

    /** Publishes the pack on the server (POST packs), then downloads it as a ZIP. */
    public async publishAndDownload(): Promise<void> {
        if (this.busy || this.df.published || !this.df.included().length) { return; }
        this.busy = true;
        let pack: PublishedPack | null;
        try {
            pack = await this.df.publish(this.packName, this.packNote);
        } finally {
            this.busy = false;
        }
        if (!pack) { return; }
        this.df.toast(`Published · ${pack.n} documents · preparing ZIP…`);
        await this.download();
    }

    /** Downloads the published pack as a ZIP (GET packs/{id}/download; can be repeated after publishing). */
    public async download(): Promise<PackZipResult | null> {
        const pack = this.df.published;
        if (!pack || this.busy) { return null; }

        this.busy = true;
        try {
            if (!this.df.azure.connected && !this.df.included().some(doc => doc.signavioFlowId)) {
                const blob = await this.api.downloadPack(pack.id);
                if (!blob) { throw new Error('API is not configured.'); }
                const fileName = safeFileName(`${pack.id} ${this.packName}`, pack.id) + '.zip';
                this.save(blob, fileName);
                this.df.toast(`Downloaded ${fileName} · ${pack.n} documents`);
                return { fileName, added: pack.n, failed: [] };
            }
            const result = await this.buildAndSave(pack);
            this.df.toast(result.failed.length
                ? `Downloaded ${result.fileName} · ${result.failed.length} file(s) could not be fetched, see README.txt`
                : `Downloaded ${result.fileName} · ${result.added} documents`);
            return result;
        } catch (e) {
            console.error('Pack ZIP failed', e);
            this.df.toast('Could not create the ZIP. Please try again.');
            return null;
        } finally {
            this.busy = false;
        }
    }

    /** Fetches every included document, builds the archive and saves it. */
    public async buildAndSave(pack: PublishedPack): Promise<PackZipResult> {
        const docs = this.df.included();
        const results = await Promise.allSettled(docs.map(d => this.files.getFile(d)));

        const entries: ZipEntry[] = [];
        const used = new Set<string>();
        const failed: FoundDocument[] = [];

        results.forEach((r, i) => {
            const d = docs[i];
            if (r.status === 'rejected') { failed.push(d); return; }
            entries.push({ path: this.uniquePath(this.folderFor(d), r.value.fileName, used), data: r.value.blob });
        });

        entries.unshift(
            { path: 'README.txt', data: this.readme(pack, docs.length - failed.length, failed) },
            // BOM so Excel opens the UTF-8 CSV with the right characters
            { path: 'manifest.csv', data: '\uFEFF' + this.df.buildCsv() }
        );

        const fileName = safeFileName(`${pack.id} ${this.packName}`, pack.id) + '.zip';
        this.save(await createZip(entries), fileName);
        return { fileName, added: docs.length - failed.length, failed };
    }

    /** Hands the file to the browser (separate method so it can be stubbed in tests). */
    public save(blob: Blob, fileName: string): void {
        downloadBlob(blob, fileName);
    }

    /** e.g. `01 Process flow/As-Is (ECC)` */
    public folderFor(d: FoundDocument): string {
        const n = String(TYPE_ORDER.indexOf(d.t) + 1).padStart(2, '0');
        return `${n} ${safeFileName(DOC_TYPES[d.t].long)}/${d.land === 'asis' ? 'As-Is (ECC)' : 'To-Be (S4HANA)'}`;
    }

    private uniquePath(folder: string, fileName: string, used: Set<string>): string {
        const safe = safeFileName(fileName);
        const dot = safe.lastIndexOf('.');
        const base = dot > 0 ? safe.slice(0, dot) : safe;
        const ext = dot > 0 ? safe.slice(dot) : '';
        let path = `${folder}/${safe}`;
        for (let i = 2; used.has(path.toLowerCase()); i++) {
            path = `${folder}/${base} (${i})${ext}`;
        }
        used.add(path.toLowerCase());
        return path;
    }

    private readme(pack: PublishedPack, added: number, failed: FoundDocument[]): string {
        const lines = [
            `Pack:       ${pack.id} · ${this.packName}`,
            `Published:  ${pack.at}`,
            `Documents:  ${added} of ${added + failed.length}`
        ];
        if (this.packNote.trim()) { lines.push('', 'Note for the workstream:', this.packNote.trim()); }
        if (failed.length) {
            lines.push('', 'These documents could not be downloaded. Fetch them from their source:');
            failed.forEach(d => lines.push(`  - ${d.name} — ${d.path}`));
        }
        lines.push('', 'manifest.csv lists every document with its type, landscape, process codes and location.');
        return lines.join('\r\n');
    }
}
